import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { API_URLS } from "./apis/urls";
import Api from "./utils/apiCall";


// Define the types for Reimbursement Request
export interface ReimbursementRequest {
    id?: number;
    AppRejDelDate?: string | null;
    ApprovedAmount?: string;
    ApprovedNumber?: string;
    ApprovedTaxExempt?: string;
    ApproverGroup?: string;
    ApproverLevel?: string;
    ApproverUserID?: string;
    CompletedDate?: string | null;
    CreatedSource?: string;
    Currency?: string;
    EmpID?: string;
    InfotypeUpdateDate?: string | null;
    LastChangeDate?: string;
    LastChangeTime?: string;
    LastChangedUserID?: string;
    PaymentDate?: string | null;
    PhaseOftheRequest?: string;
    ReasonCode?: string;
    ReferenceNumber?: string;
    ReimbursementType?: string;
    RequestType?: string;
    RequestStatus?: string;
    RequestedAmount?: string;
    RequestedNumber?: string;
    RequestedTaxExempt?: string;
    RequestCreationDate?: string;
    Request_Status?: string;
    Reimbursement_Type?: string;
    Request_Type?: string;
    created_at?: string;
    modified_at?: string;
}

// Define the interface for Reimbursement Type
export interface ReimbursementType {
    ReimbursementType?: string;
    Ddtext?: string;
    reimbursement_type_id?: string;
    reimbursement_type_name?: string;
    type_id?: string;
    type_text?: string;
}

// Define the interface for Cascading Masters
export interface CascadingMaster {
    created_at?: string;
    modified_at?: string;
    deleted_at?: string | null;
    id?: number;
    reimbursement_type: string;
    reimbursement_type_text: string;
    request_type_id: string;
    request_type_text: string;
    ltc_scheme_code?: string;
    ltc_scheme_text?: string;
    ltc_block_type?: string;
}

// Define the interface for Request Type
export interface RequestType {
    request_type_id: string;
    request_type_text: string;
    reimbursement_type?: string;
    reimbursement_type_text?: string;
    [key: string]: any;
}

// Define the interface for LTC Scheme
export interface LTCScheme {
    ltc_scheme_code: string;
    ltc_scheme_text: string;
    request_type_id?: string;
    [key: string]: any;
}

// Define the interface for Block Type
export interface BlockType {
    ltc_block_type: string;
    ltc_scheme_code?: string;
    [key: string]: any;
}

// Define the interface for Dynamic Field
export interface DynamicField {
    Label: string;
    ReimbursementType: string;
    InformationText: string;
    RequestType: string;
    LTCSchemeCode: string;
    HeaderCustomerField: string;
    LineCustomerField?: string; // Field name for line items (multiline table)
    EndDate: string;
    StartDate: string;
    CustFldMandatory: boolean;
    ViewOfFieldType: string;
    WidthOnESSScreen: string;
    VisibleToEmployee: boolean;
    EnabledForEmployee: boolean;
    ReadOnlyForEmp: boolean;
    ReadOnlyForEmployee?: boolean; // Alias for ReadOnlyForEmp
    FieldVisibleToApp: boolean;
    FieldEnableForApp: boolean;
    ReadOnlyForApp: boolean;
    FieldsOrder: string;
    ActionName: string;
    DefaultValue: string;
    FunctionModule: string;
    UIPosition: string;
    OptionsValue?: {
        results: any[];
    };
    TextLine?: string;
}

// Define the interface for Config Data
export interface ConfigData {
    created_at: string;
    modified_at: string;
    deleted_at: string | null;
    id: number;
    ReimbursementType: string;
    AllowDeclaration: string;
    RequestType: string;
    AllowDependantFlag: string;
    LTCSchemeCode: string;
    ShowDeptAtLevel: string;
    AllowAttachment: string;
    DynamicFields: string; // JSON string that needs to be parsed
    parsedDynamicFields?: DynamicField[]; // Parsed dynamic fields array
    MultilineTableHeaders?: string; // JSON string that needs to be parsed
    parsedMultilineTableHeaders?: DynamicField[]; // Parsed multiline table headers array
}



// Define the interface for Dependant
export interface Dependant {
    EmpID?: string;
    FamilyMemberText?: string;
    DependentSelected?: boolean;
    ClaimComplete?: boolean;
    ReadOnly?: boolean;
    FamilyMember?: string;
    Name?: string;
    ObjectID?: string;
    FamsaObjps?: string;
    BirthDate?: string;
    CountryOfBirth?: string;
    Country?: string;
    Nationality?: string;
    NationalityDescription?: string;
    Gender?: string;
    FirstName?: string;
    LastName?: string;
    FullName?: string;
    AgeYears?: string;
    AgeMonths?: string;
    AgeDays?: string;
    Disabled?: boolean;
    MaritalStatus?: string;
    AnnualIncome?: string | number;
    Currency?: string;
    [key: string]: any;
}

// Define the interface for Dynamic Config
export interface DynamicConfig {
    configData: ConfigData;
    fieldTypes: {
        [key: string]: string;
    };
    toDependants: Dependant[];
}

// Define the parameters for fetching dynamic config
export interface FetchDynamicConfigParams {
    ReferenceNumber?: string;
    ReimbursementType: string;
    RequestType: string;
    LTCSchemeCode?: string;
    EmpID?: string;
    RequestOperation?: string;
}



export interface ReimbursementState {
    requests: ReimbursementRequest[];
    currentRequest: ReimbursementRequest | null;
    reimbursementTypes: ReimbursementType[];
    requestTypes: RequestType[];
    ltcSchemes: LTCScheme[];
    blockTypes: BlockType[];
    dynamicConfig: DynamicConfig | null;
    cascadingMasters: CascadingMaster[];
    reimbursementMasters: {
        Request_Phase: any[];
        Request_Status: any[];
        Request_Type: any[];
        Reimbursement_Type: any[];
    } | null;
    mastersLoading: boolean;
    reimbursementEligibility: any[];
    ltcEligibility: any[];
    eligibilityLoading: boolean;
    loading: boolean;
    viewDetailsLoading: boolean;
    error: string | null;
    createLoading: boolean;
    uploadLoading: boolean;
    deleteLoading: boolean;
    reimbursementTypesLoading: boolean;
    requestTypesLoading: boolean;
    ltcSchemesLoading: boolean;
    blockTypesLoading: boolean;
    dynamicConfigLoading: boolean;
    cascadingMastersLoading: boolean;
    associatedRefData: any[] | null;
    associatedRefLoading: boolean;
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
}

// Define the API response type
interface ReimbursementResponse {
    data: ReimbursementRequest[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

// Define the request parameters
interface FindAllReimbursementParams {
    page?: number;
    limit?: number;
    ReferenceNumber?: string;
    ReimbursementType?: string;
    RequestType?: string;
    PhaseOftheRequest?: string;
    RequestStatus?: string;
    startDate?: string;
    endDate?: string;
    orderColumn?: string;
    orderBy?: string;
    query?: string;
}

// Define the create Reimbursement request parameters
interface CreateReimbursementParams {
    ReferenceNumber?: string;
    ReimbursementType?: string;
    RequestType?: string;
    PhaseOftheRequest?: string;
    RequestStatus?: string;
    EmpID?: string;
    ApprovedAmount?: string;
    Currency?: string;
    [key: string]: any;
}

// File upload parameters
interface FileUploadParams {
    filestring: string;
}

// File delete parameters
interface FileDeleteParams {
    key: string;
}

// Reimbursement request delete parameters
interface DeleteReimbursementParams {
    id: number;
    ReferenceNumber?: string;
}

// Initial state
const initialState: ReimbursementState = {
    requests: [],
    currentRequest: null,
    reimbursementTypes: [],
    requestTypes: [],
    ltcSchemes: [],
    blockTypes: [],
    dynamicConfig: null,
    cascadingMasters: [],
    reimbursementMasters: null,
    mastersLoading: false,
    reimbursementEligibility: [],
    ltcEligibility: [],
    eligibilityLoading: false,
    loading: false,
    viewDetailsLoading: false,
    error: null,
    createLoading: false,
    uploadLoading: false,
    deleteLoading: false,
    reimbursementTypesLoading: false,
    requestTypesLoading: false,
    ltcSchemesLoading: false,
    blockTypesLoading: false,
    dynamicConfigLoading: false,
    cascadingMastersLoading: false,
    associatedRefData: null,
    associatedRefLoading: false,
    pagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
    },
};

// Async thunk for fetching Reimbursement requests
export const fetchAllReimbursement = createAsyncThunk(
    "reimbursement/fetchAllReimbursement",
    async (params: FindAllReimbursementParams = {}, { rejectWithValue }) => {
        try {
            const payload = {
                page: params.page || 1,
                limit: params.limit || 10,
                orderColumn: params.orderColumn || "",
                orderBy: params.orderBy || "",
                ReferenceNumber: params.ReferenceNumber || "",
                ReimbursementType: params.ReimbursementType || "",
                RequestType: params.RequestType || "",
                RequestStatus: params.RequestStatus || "",
                PhaseOftheRequest: params.PhaseOftheRequest || "",
                startDate: params.startDate || "",
                endDate: params.endDate || ""
            };
            const response = await Api.post(
                API_URLS.reimbursement.FindAllReimbursement,
                payload
            );
            // Handle the nested response structure
            const responseData = response.data;
            console.log("responseData:", responseData);


            let resultContainer = null;

            if (responseData && responseData.data) {
                // If the data property is the results object (containing data array and total)
                if (responseData.data.data && Array.isArray(responseData.data.data)) {
                    resultContainer = responseData.data;
                }
                // If we need to go one level deeper (responseData.data is the {status, message, data: {...}} object)
                else if (responseData.data.data && typeof responseData.data.data === 'object' && responseData.data.data.data && Array.isArray(responseData.data.data.data)) {
                    resultContainer = responseData.data.data;
                }
                // Case where responseData.data is directly the array
                else if (Array.isArray(responseData.data)) {
                    return {
                        data: responseData.data,
                        total: responseData.data.length,
                        page: 1,
                        limit: responseData.data.length,
                        totalPages: 1,
                    } as ReimbursementResponse;
                }
            }

            if (resultContainer) {
                return {
                    data: resultContainer.data || [],
                    total: resultContainer.total || 0,
                    page: resultContainer.page || 1,
                    limit: resultContainer.limit || 10,
                    totalPages: resultContainer.totalPages || 0,
                } as ReimbursementResponse;
            }

            // Fallback for unexpected structures
            console.warn("Unexpected data structure, returning empty data:", responseData);
            return {
                data: [],
                total: 0,
                page: 1,
                limit: 10,
                totalPages: 0,
            } as ReimbursementResponse;
        } catch (error: any) {
            // Check if it's a network error or specific error type
            const errorMessage = Api.getErrorMessage(error);

            // Only log non-critical errors, don't show them to user
            if (error.response?.status >= 500) {
                console.error("Server error:", error);
            } else if (error.response?.status >= 400 && error.response?.status < 500) {
                console.warn("Client error:", error);
            } else {
                console.warn("Network or other error:", error);
            }

            // Return empty data instead of rejecting to prevent error display
            // Only reject for critical errors that need user attention
            if (error.response?.status === 401 || error.response?.status === 403) {
                return rejectWithValue(errorMessage);
            }

            // For other errors, return empty data silently
            return {
                data: [],
                total: 0,
                page: 1,
                limit: 10,
                totalPages: 0,
            } as ReimbursementResponse;
        }
    }
);

// Async thunk for fetching Reimbursement Types
export const fetchReimbursementTypes = createAsyncThunk(
    "reimbursement/fetchReimbursementTypes",
    async (empIdArg: string | undefined, { rejectWithValue }) => {
        try {
            let empId = empIdArg;
            if (!empId) {
                try {
                    const storedUser = localStorage.getItem("userData") || localStorage.getItem("user") || "{}";
                    const userJson = JSON.parse(storedUser);
                    empId = userJson?.details?.empId || userJson?.empId || userJson?.usrid || userJson?.id || "";
                } catch (e) {
                    console.error("Error reading user for reimbursement types:", e);
                }
            }

            const payload = empId ? { empId } : {};
            const response = await Api.post(
                API_URLS.reimbursement.REIMBURSEMENT_ELIGIBILITY,
                payload
            );

            // Handle different response structures
            let types: ReimbursementType[] = [];
            const responseData = response.data;
            if (responseData?.data?.data && Array.isArray(responseData.data.data)) {
                types = responseData.data.data;
            } else if (responseData?.data && Array.isArray(responseData.data)) {
                types = responseData.data;
            } else if (Array.isArray(responseData)) {
                types = responseData;
            }

            // Sort reimbursement types alphabetically by name
            types.sort((a: any, b: any) => {
                const nameA = String(a.reimbursement_type_name || a.Ddtext || a.type_text || a.reimbursement_type_text || "").toLowerCase();
                const nameB = String(b.reimbursement_type_name || b.Ddtext || b.type_text || b.reimbursement_type_text || "").toLowerCase();
                return nameA.localeCompare(nameB);
            });

            return types;
        } catch (error: any) {
            console.log("Error fetching reimbursement types:", error);
            return rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

// Async thunk for fetching Request Types based on Reimbursement Type
export const fetchRequestTypes = createAsyncThunk(
    "reimbursement/fetchRequestTypes",
    async (reimbursement_type: string, { rejectWithValue }) => {
        try {
            const response = await Api.post(
                API_URLS.reimbursement.GET_MASTERS_CASCADING,
                { reimbursement_type }
            );
            console.log("Request Types response:", response);

            // Handle different response structures
            let requestTypes: RequestType[] = [];
            if (response.data?.data?.data && Array.isArray(response.data.data.data)) {
                requestTypes = response.data.data.data.map((item: CascadingMaster) => ({
                    request_type_id: item.request_type_id,
                    request_type_text: item.request_type_text,
                    reimbursement_type: item.reimbursement_type,
                    reimbursement_type_text: item.reimbursement_type_text,
                }));
            } else if (response.data?.data && Array.isArray(response.data.data)) {
                requestTypes = response.data.data.map((item: CascadingMaster) => ({
                    request_type_id: item.request_type_id,
                    request_type_text: item.request_type_text,
                    reimbursement_type: item.reimbursement_type,
                    reimbursement_type_text: item.reimbursement_type_text,
                }));
            } else if (Array.isArray(response.data)) {
                requestTypes = response.data.map((item: CascadingMaster) => ({
                    request_type_id: item.request_type_id,
                    request_type_text: item.request_type_text,
                    reimbursement_type: item.reimbursement_type,
                    reimbursement_type_text: item.reimbursement_type_text,
                }));
            }

            // Remove duplicates based on request_type_id
            const uniqueRequestTypes = requestTypes.filter(
                (type, index, self) =>
                    index === self.findIndex((t) => t.request_type_id === type.request_type_id)
            );

            return uniqueRequestTypes;
        } catch (error: any) {
            console.log("Error fetching request types:", error);
            return rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

// Async thunk for fetching LTC Schemes based on Request Type ID
export const fetchLTCSchemes = createAsyncThunk(
    "reimbursement/fetchLTCSchemes",
    async (request_type_id: string, { rejectWithValue }) => {
        try {
            const response = await Api.post(
                API_URLS.reimbursement.GET_MASTERS_CASCADING,
                { request_type_id }
            );
            console.log("LTC Schemes response:", response);

            // Handle different response structures
            let ltcSchemes: LTCScheme[] = [];
            if (response.data?.data?.data && Array.isArray(response.data.data.data)) {
                ltcSchemes = response.data.data.data
                    .filter((item: CascadingMaster) => item.ltc_scheme_code && item.ltc_scheme_code !== '')
                    .map((item: CascadingMaster) => ({
                        ltc_scheme_code: item.ltc_scheme_code || '',
                        ltc_scheme_text: item.ltc_scheme_text || '',
                        request_type_id: item.request_type_id,
                    }));
            } else if (response.data?.data && Array.isArray(response.data.data)) {
                ltcSchemes = response.data.data
                    .filter((item: CascadingMaster) => item.ltc_scheme_code && item.ltc_scheme_code !== '')
                    .map((item: CascadingMaster) => ({
                        ltc_scheme_code: item.ltc_scheme_code || '',
                        ltc_scheme_text: item.ltc_scheme_text || '',
                        request_type_id: item.request_type_id,
                    }));
            } else if (Array.isArray(response.data)) {
                ltcSchemes = response.data
                    .filter((item: CascadingMaster) => item.ltc_scheme_code && item.ltc_scheme_code !== '')
                    .map((item: CascadingMaster) => ({
                        ltc_scheme_code: item.ltc_scheme_code || '',
                        ltc_scheme_text: item.ltc_scheme_text || '',
                        request_type_id: item.request_type_id,
                    }));
            }

            // Remove duplicates based on ltc_scheme_code
            const uniqueLTCSchemes = ltcSchemes.filter(
                (scheme, index, self) =>
                    index === self.findIndex((s) => s.ltc_scheme_code === scheme.ltc_scheme_code)
            );

            return uniqueLTCSchemes;
        } catch (error: any) {
            console.log("Error fetching LTC schemes:", error);
            return rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

// Async thunk for fetching Block Types based on LTC Scheme Code
export const fetchBlockTypes = createAsyncThunk(
    "reimbursement/fetchBlockTypes",
    async (ltc_scheme_code: string, { rejectWithValue }) => {
        try {
            // First attempt: Call GET_LTC_CODE ('employee-reimbursement/reimbursement-ltc-block-master')
            // which matches mobile app (ServiceRequestType.GET_LTC_CODE).
            let rawList: any[] = [];
            try {
                const ltcCodeRes = await Api.post(
                    API_URLS.reimbursement.GET_LTC_CODE,
                    {}
                );
                console.log("GET_LTC_CODE response:", ltcCodeRes);
                const ltcData = ltcCodeRes.data?.data?.data || ltcCodeRes.data?.data || ltcCodeRes.data;
                if (Array.isArray(ltcData) && ltcData.length > 0) {
                    rawList = ltcData;
                }
            } catch (err) {
                console.warn("GET_LTC_CODE failed, falling back to GET_MASTERS_CASCADING:", err);
            }

            // Fallback: If GET_LTC_CODE returned no records, call GET_MASTERS_CASCADING
            if (!rawList || rawList.length === 0) {
                const response = await Api.post(
                    API_URLS.reimbursement.GET_MASTERS_CASCADING,
                    { ltc_scheme_code }
                );
                console.log("Block Types cascading response:", response);
                rawList = response.data?.data?.data || response.data?.data || response.data || [];
            }

            let blockTypes: BlockType[] = [];
            if (Array.isArray(rawList)) {
                blockTypes = rawList
                    .filter((item: any) => item && (item.ltc_block_type || item.StartDate || item.startDate))
                    .map((item: any, idx: number) => ({
                        ltc_block_type: item.ltc_block_type || item.block_type || item.id || `Block_${idx + 1}`,
                        ltc_scheme_code: item.ltc_scheme_code || ltc_scheme_code,
                        ...item, // Preserve StartDate, EndDate, and all other properties
                    }));
            }

            // Deduplicate: If multiple rows carry the exact same StartDate, EndDate and ltc_block_type,
            // keep unique ones; but ensure distinct blocks (different dates/ids) are preserved!
            const uniqueBlockTypes = blockTypes.filter(
                (block, index, self) =>
                    index === self.findIndex((b) => {
                        const bKey = `${b.ltc_block_type}_${b.StartDate || b.startDate || ''}_${b.EndDate || b.endDate || ''}_${b.id ?? ''}`;
                        const curKey = `${block.ltc_block_type}_${block.StartDate || block.startDate || ''}_${block.EndDate || block.endDate || ''}_${block.id ?? ''}`;
                        return bKey === curKey;
                    })
            );

            return uniqueBlockTypes;
        } catch (error: any) {
            console.log("Error fetching block types:", error);
            return rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

// Async thunk for fetching Dynamic Config
export const fetchDynamicConfig = createAsyncThunk(
    "reimbursement/fetchDynamicConfig",
    async (params: FetchDynamicConfigParams, { rejectWithValue }) => {
        try {
            let empId = params.EmpID;
            if (!empId) {
                try {
                    const userJson = JSON.parse(localStorage.getItem("user") || "{}");
                    empId = userJson?.details?.empId || userJson?.empId || userJson?.usrid || "";
                } catch (e) {
                    console.error("Error reading user for dynamic config:", e);
                }
            }

            const payload = {
                ReferenceNumber: params.ReferenceNumber || "",
                ReimbursementType: params.ReimbursementType || "",
                RequestType: params.RequestType || "",
                LTCSchemeCode: params.LTCSchemeCode || "",
                EmpID: empId || "",
                RequestOperation: params.RequestOperation || "EC",
            };

            const response = await Api.post(
                API_URLS.reimbursement.GET_CONFIG,
                payload
            );
            console.log("Dynamic Config response:", response);

            // Parse the response data
            let responseData = response.data?.data || response.data;

            // Handle double nesting if present (response.data.data.data or response.data.data.configData)
            if (responseData && responseData.data && !responseData.configData && !responseData.DynamicFields) {
                responseData = responseData.data;
            }

            // Extract config data if it's nested
            const config = responseData?.configData || responseData;

            // Parse DynamicFields JSON string if it exists
            if (config?.DynamicFields) {
                try {
                    const parsedFields = JSON.parse(config.DynamicFields);
                    config.parsedDynamicFields = parsedFields;
                } catch (error) {
                    console.error("Error parsing DynamicFields JSON:", error);
                    config.parsedDynamicFields = [];
                }
            }
            // Parse MultilineTableHeaders JSON string if it exists
            if (config?.MultilineTableHeaders) {
                try {
                    const parsedHeaders = JSON.parse(config.MultilineTableHeaders);
                    config.parsedMultilineTableHeaders = Array.isArray(parsedHeaders) ? parsedHeaders : [];
                } catch (error) {
                    console.error("Error parsing MultilineTableHeaders JSON:", error);
                    config.parsedMultilineTableHeaders = [];
                }
            }

            // Parse TermCondition JSON string if it exists
            if (config?.TermCondition) {
                try {
                    const parsedTerms = JSON.parse(config.TermCondition);
                    config.parsedTermCondition = Array.isArray(parsedTerms) ? parsedTerms : [];
                } catch (error) {
                    console.error("Error parsing TermCondition JSON:", error);
                    config.parsedTermCondition = [];
                }
            }

            // Normalize toDependants array safely
            const dependants =
                response.data?.toDependants ||
                response.data?.data?.toDependants ||
                responseData?.toDependants ||
                config?.toDependants ||
                [];
            responseData.toDependants = Array.isArray(dependants) ? dependants : [];

            // Ensure configData property exists for compatibility with component without creating circular references
            if (!responseData.configData && config) {
                return { ...responseData, configData: { ...config } };
            }

            return responseData as DynamicConfig;


        } catch (error: any) {
            console.log("Error fetching dynamic config:", error);
            return rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

// Async thunk for syncing reimbursement data from backend
export const syncReimbursementData = createAsyncThunk(
    "reimbursement/syncData",
    async (empId: string, { rejectWithValue }) => {
        try {
            const payload = {
                "empId": empId
            };
            const response = await Api.post(
                API_URLS.reimbursement.SYNC_DATA,
                payload
            );
            console.log("Sync response:", response);
            return response.data;
        } catch (error: any) {
            console.log("Error syncing reimbursement data:", error);
            return rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

// Async thunk for syncing eligibility data
export const syncEligibility = createAsyncThunk(
    "reimbursement/syncEligibility",
    async (empId: string, { rejectWithValue }) => {
        try {
            const payload = {
                "empId": empId
            };
            const response = await Api.post(
                API_URLS.reimbursement.SYNC_ELIGIBILITY,
                payload
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

// Async thunk for syncing reimbursement types (matching mobile's SYNC_REIMBURSEMENT_TYPES)
export const syncReimbursementTypes = createAsyncThunk(
    "reimbursement/syncReimbursementTypes",
    async (empId: string, { rejectWithValue }) => {
        try {
            const payload = {
                empId: empId
            };
            const response = await Api.post(
                API_URLS.reimbursement.SYNC_REIMBURSEMENT_TYPES,
                payload
            );
            console.log("Sync reimbursement types response:", response);
            return response.data;
        } catch (error: any) {
            console.log("Error syncing reimbursement types:", error);
            return rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

// Async thunk for fetching LTC eligibility data
export const fetchLTCEligibility = createAsyncThunk(
    "reimbursement/fetchLTCEligibility",
    async (empId: string, { rejectWithValue }) => {
        try {
            const payload = {
                "empId": empId
            };
            const response = await Api.post(
                API_URLS.reimbursement.LTC_ELIGIBILITY,
                payload
            );
            return response.data?.data?.data || response.data?.data || [];
        } catch (error: any) {
            return rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

// Async thunk for fetching Reimbursement eligibility data
export const fetchReimbursementEligibility = createAsyncThunk(
    "reimbursement/fetchReimbursementEligibility",
    async (empId: string, { rejectWithValue }) => {
        try {
            const payload = {
                "empId": empId
            };
            const response = await Api.post(
                API_URLS.reimbursement.REIMBURSEMENT_ELIGIBILITY,
                payload
            );
            return response.data?.data?.data || response.data?.data || [];
        } catch (error: any) {
            return rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

// Async thunk for fetching all reimbursement masters at once
export const fetchReimbursementMasters = createAsyncThunk(
    "reimbursement/fetchMasters",
    async (_, { rejectWithValue }) => {
        try {
            const payload = {
                "type": ""
            };
            const response = await Api.post(
                API_URLS.reimbursement.GET_MASTERS,
                payload
            );
            console.log("Masters response:", response);
            return response.data?.data?.data || response.data?.data;
        } catch (error: any) {
            console.log("Error fetching reimbursement masters:", error);
            return rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

// Async thunk for fetching Cascading Masters (kept for backward compatibility)
export const fetchCascadingMasters = createAsyncThunk(
    "reimbursement/fetchCascadingMasters",
    async (_, { rejectWithValue }) => {
        try {
            const response = await Api.post(
                API_URLS.reimbursement.GET_MASTERS_CASCADING,
                {}
            );
            console.log("Cascading Masters response:", response);

            // Handle different response structures
            let masters: CascadingMaster[] = [];
            if (response.data?.data?.data && Array.isArray(response.data.data.data)) {
                masters = response.data.data.data;
            } else if (response.data?.data && Array.isArray(response.data.data)) {
                masters = response.data.data;
            } else if (Array.isArray(response.data)) {
                masters = response.data;
            }

            return masters;
        } catch (error: any) {
            console.log("Error fetching cascading masters:", error);
            return rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

// Async thunk for creating Reimbursement request

export const createReimbursementRequest = createAsyncThunk(
    "reimbursement/createRequest",
    async (
        params: CreateReimbursementParams,
        { rejectWithValue }
    ) => {
        try {
            console.log("Creating reimbursement request:", params);

            const response = await Api.post(
                API_URLS.reimbursement.CREATE_OR_UPDATE,
                params
            );

            console.log("Reimbursement response:", response.data);

            return response.data;
        } catch (error: any) {
            console.error(
                "Error creating reimbursement request:",
                error
            );

            console.error(
                "Error response:",
                error?.response
            );

            console.error(
                "Error response data:",
                error?.response?.data
            );

            console.error(
                "Error response status:",
                error?.response?.status
            );

            // Request details
            if (error?.config) {
                console.error(
                    "Request URL:",
                    error.config.url
                );

                console.error(
                    "Request method:",
                    error.config.method
                );

                console.error(
                    "Request headers:",
                    error.config.headers
                );

                console.error(
                    "Request body sent:",
                    error.config.data
                );
            }

            return rejectWithValue(
                error?.response?.data || Api.getErrorMessage(error)
            );
        }
    }
);

// Async thunk for fetching a single reimbursement request by reference number
export const fetchReimbursementById = createAsyncThunk(
    "reimbursement/fetchReimbursementById",
    async (referenceNumber: string, { rejectWithValue }) => {
        try {
            const response = await Api.post(
                API_URLS.reimbursement.REIMBURSEMENT_REQUEST_BY_ID,
                { referenceNumber }
            );
            const responseData = response.data?.data?.data ?? response.data?.data ?? response.data;
            return responseData;
        } catch (error: any) {
            return rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

// Async thunk for syncing a single reimbursement by reference number (populates ToDynamics)
export const syncReimbursementByRef = createAsyncThunk(
    "reimbursement/syncReimbursementByRef",
    async (referenceNumber: string, { rejectWithValue }) => {
        try {
            const response = await Api.post(
                API_URLS.reimbursement.SYNC_REIMBURSEMENT_BY_REF,
                { referenceNumber }
            );
            return response.data?.data ?? response.data;
        } catch (error: any) {
            return rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

// Async thunk for syncing dependents for an employee
// Mobile: ServiceRequestType.SYNC_REIMBURSEMENT = 'employee-reimbursement/sync-reimbursement-request'
// Payload: { empId: user?.details?.empId }
// This is the CORRECT endpoint for syncing dependents on the creation form —
// different from syncReimbursementData which hits 'sync-data'.
export const syncReimbursementRequest = createAsyncThunk(
    "reimbursement/syncReimbursementRequest",
    async (empId: string, { rejectWithValue }) => {
        try {
            const response = await Api.post(
                API_URLS.reimbursement.SYNC_REIMBURSEMENT_REQUEST,
                { empId }
            );
            return response.data?.data ?? response.data;
        } catch (error: any) {
            return rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

// Async thunk for downloading the reimbursement form PDF
export const downloadReimbursementForm = createAsyncThunk(
    "reimbursement/downloadReimbursementForm",
    async (params: { empId: string; referenceNumber: string }, { rejectWithValue }) => {
        try {
            const response = await Api.post(
                API_URLS.reimbursement.DOWNLOAD_REIMBURSEMENT_FORM,
                { empId: params.empId, referenceNumber: params.referenceNumber }
            );
            return response.data?.data ?? response.data;
        } catch (error: any) {
            return rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

// Async thunk for fetching Associated Reference
export const fetchReimbursementAssocRef = createAsyncThunk(
    "reimbursement/fetchReimbursementAssocRef",
    async (params: { empId: string; reimbursementType: string; ltcSchemeCode?: string; forceSync?: boolean }, { rejectWithValue }) => {
        try {
            const response = await Api.post(
                API_URLS.reimbursement.GET_REIMBURSEMENT_ASSOC_REF,
                params
            );
            return response.data?.data?.data ?? response.data?.data ?? response.data;
        } catch (error: any) {
            return rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

// Async thunk for uploading file to AWS
export const uploadFileToAWS = createAsyncThunk(
    "reimbursement/uploadFile",
    async (params: FileUploadParams, { rejectWithValue }) => {
        try {
            const response = await Api.post(API_URLS.AWS_BUCKET.UPLOAD_FILE, params);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

// Async thunk for deleting file from AWS
export const deleteFileFromAWS = createAsyncThunk(
    "reimbursement/deleteFile",
    async (params: FileDeleteParams, { rejectWithValue }) => {
        try {
            const response = await Api.post(API_URLS.AWS_BUCKET.DELETE_FILE, params);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

// Async thunk for deleting Reimbursement request
export const deleteReimbursementRequest = createAsyncThunk(
    "reimbursement/deleteRequest",
    async (params: DeleteReimbursementParams, { rejectWithValue }) => {
        try {
            const requestBody = {
                id: params.id,
                ReferenceNumber: params.ReferenceNumber || "",
            };
            const response = await Api.post(
                API_URLS.reimbursement.DELETE,
                requestBody
            );
            return { ...response.data, deletedId: params.id };
        } catch (error: any) {
            return rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

// Create the slice

const reimbursementSlice = createSlice({
    name: "reimbursement",
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearCurrentRequest: (state) => {
            state.currentRequest = null;
        },
        setCurrentRequest: (state, action: PayloadAction<number>) => {
            const requestId = action.payload;
            const request = state.requests.find((req) => req.id === requestId);
            state.currentRequest = request || null;
        },
        setPage: (state, action: PayloadAction<number>) => {
            state.pagination.page = action.payload;
        },
        setLimit: (state, action: PayloadAction<number>) => {
            state.pagination.limit = action.payload;
        },
        clearRequestTypes: (state) => {
            state.requestTypes = [];
            state.ltcSchemes = [];
        },
        clearLTCSchemes: (state) => {
            state.ltcSchemes = [];
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch Reimbursement requests
            .addCase(fetchAllReimbursement.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAllReimbursement.fulfilled, (state, action) => {
                state.loading = false;
                state.requests = action.payload.data;
                state.pagination = {
                    page: action.payload.page,
                    limit: action.payload.limit,
                    total: action.payload.total,
                    totalPages: action.payload.totalPages,
                };
            })
            .addCase(fetchAllReimbursement.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            // Sync Reimbursement Data
            .addCase(syncReimbursementData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            // Sync Eligibility
            .addCase(syncEligibility.pending, (state) => {
                state.eligibilityLoading = true;
            })
            .addCase(syncEligibility.fulfilled, (state) => {
                state.eligibilityLoading = false;
            })
            .addCase(syncEligibility.rejected, (state, action) => {
                state.eligibilityLoading = false;
                state.error = action.payload as string;
            })
            // Fetch LTC Eligibility
            .addCase(fetchLTCEligibility.pending, (state) => {
                state.eligibilityLoading = true;
            })
            .addCase(fetchLTCEligibility.fulfilled, (state, action) => {
                state.eligibilityLoading = false;
                state.ltcEligibility = action.payload;
            })
            .addCase(fetchLTCEligibility.rejected, (state, action) => {
                state.eligibilityLoading = false;
                state.error = action.payload as string;
            })
            // Fetch Reimbursement Eligibility
            .addCase(fetchReimbursementEligibility.pending, (state) => {
                state.eligibilityLoading = true;
            })
            .addCase(fetchReimbursementEligibility.fulfilled, (state, action) => {
                state.eligibilityLoading = false;
                state.reimbursementEligibility = action.payload;
            })
            .addCase(fetchReimbursementEligibility.rejected, (state, action) => {
                state.eligibilityLoading = false;
                state.error = action.payload as string;
            })
            // Fetch Reimbursement Types
            .addCase(fetchReimbursementTypes.pending, (state) => {
                state.reimbursementTypesLoading = true;
                state.error = null;
            })
            .addCase(fetchReimbursementTypes.fulfilled, (state, action) => {
                state.reimbursementTypesLoading = false;
                state.reimbursementTypes = action.payload;
            })
            .addCase(fetchReimbursementTypes.rejected, (state, action) => {
                state.reimbursementTypesLoading = false;
                state.error = action.payload as string;
            })
            // Fetch Request Types
            .addCase(fetchRequestTypes.pending, (state) => {
                state.requestTypesLoading = true;
                state.error = null;
                state.requestTypes = []; // Clear previous request types
                state.ltcSchemes = []; // Clear LTC schemes when fetching new request types
                state.blockTypes = []; // Clear block types when fetching new request types
            })
            .addCase(fetchRequestTypes.fulfilled, (state, action) => {
                state.requestTypesLoading = false;
                state.requestTypes = action.payload;
            })
            .addCase(fetchRequestTypes.rejected, (state, action) => {
                state.requestTypesLoading = false;
                state.error = action.payload as string;
            })
            // Fetch LTC Schemes
            .addCase(fetchLTCSchemes.pending, (state) => {
                state.ltcSchemesLoading = true;
                state.error = null;
                state.ltcSchemes = []; // Clear previous LTC schemes
                state.blockTypes = []; // Clear block types when fetching new LTC schemes
            })
            .addCase(fetchLTCSchemes.fulfilled, (state, action) => {
                state.ltcSchemesLoading = false;
                state.ltcSchemes = action.payload;
            })
            .addCase(fetchLTCSchemes.rejected, (state, action) => {
                state.ltcSchemesLoading = false;
                state.error = action.payload as string;
            })
            // Fetch Block Types
            .addCase(fetchBlockTypes.pending, (state) => {
                state.blockTypesLoading = true;
                state.error = null;
                state.blockTypes = []; // Clear previous block types
            })
            .addCase(fetchBlockTypes.fulfilled, (state, action) => {
                state.blockTypesLoading = false;
                state.blockTypes = action.payload;
            })
            .addCase(fetchBlockTypes.rejected, (state, action) => {
                state.blockTypesLoading = false;
                state.error = action.payload as string;
            })
            // Fetch Dynamic Config
            .addCase(fetchDynamicConfig.pending, (state) => {
                state.dynamicConfigLoading = true;
                state.error = null;
            })
            .addCase(fetchDynamicConfig.fulfilled, (state, action) => {
                state.dynamicConfigLoading = false;
                state.dynamicConfig = action.payload;
            })
            .addCase(fetchDynamicConfig.rejected, (state, action) => {
                state.dynamicConfigLoading = false;
                state.error = action.payload as string;
            })
            // Fetch Cascading Masters
            .addCase(fetchCascadingMasters.pending, (state) => {
                state.cascadingMastersLoading = true;
                state.error = null;
            })
            .addCase(fetchCascadingMasters.fulfilled, (state, action) => {
                state.cascadingMastersLoading = false;
                state.cascadingMasters = action.payload;
            })
            .addCase(fetchCascadingMasters.rejected, (state, action) => {
                state.cascadingMastersLoading = false;
                state.error = action.payload as string;
            })
            // Create Reimbursement request
            .addCase(createReimbursementRequest.pending, (state) => {
                state.createLoading = true;
            })
            .addCase(createReimbursementRequest.fulfilled, (state) => {
                state.createLoading = false;
            })
            .addCase(createReimbursementRequest.rejected, (state, action) => {
                state.createLoading = false;
            })
            // Upload file to AWS
            .addCase(uploadFileToAWS.pending, (state) => {
                state.uploadLoading = true;
                state.error = null;
            })
            .addCase(uploadFileToAWS.fulfilled, (state) => {
                state.uploadLoading = false;
            })
            .addCase(uploadFileToAWS.rejected, (state, action) => {
                state.uploadLoading = false;
                state.error = action.payload as string;
            })
            // Delete file from AWS
            .addCase(deleteFileFromAWS.pending, (state) => {
                state.deleteLoading = true;
                state.error = null;
            })
            .addCase(deleteFileFromAWS.fulfilled, (state) => {
                state.deleteLoading = false;
            })
            .addCase(deleteFileFromAWS.rejected, (state, action) => {
                state.deleteLoading = false;
                state.error = action.payload as string;
            })
            // Delete Reimbursement request
            .addCase(deleteReimbursementRequest.pending, (state) => {
                state.deleteLoading = true;
                state.error = null;
            })
            .addCase(deleteReimbursementRequest.fulfilled, (state, action) => {
                state.deleteLoading = false;
                // Remove the deleted request from the state
                state.requests = state.requests.filter(
                    (request) => request.id !== action.payload.deletedId
                );
                // Update pagination total
                state.pagination.total = Math.max(0, state.pagination.total - 1);
            })
            .addCase(deleteReimbursementRequest.rejected, (state, action) => {
                state.deleteLoading = false;
                state.error = action.payload as string;
            })
            // Fetch Reimbursement Masters (Batch)
            .addCase(fetchReimbursementMasters.pending, (state) => {
                state.mastersLoading = true;
                state.error = null;
            })
            .addCase(fetchReimbursementMasters.fulfilled, (state, action) => {
                state.mastersLoading = false;
                state.reimbursementMasters = action.payload;
            })
            .addCase(fetchReimbursementMasters.rejected, (state, action) => {
                state.mastersLoading = false;
                state.error = action.payload as string;
            })
            // Fetch Reimbursement By ID
            .addCase(fetchReimbursementById.pending, (state) => {
                state.viewDetailsLoading = true;
                state.error = null;
            })
            .addCase(fetchReimbursementById.fulfilled, (state) => {
                state.viewDetailsLoading = false;
            })
            .addCase(fetchReimbursementById.rejected, (state, action) => {
                state.viewDetailsLoading = false;
                state.error = action.payload as string;
            })
            // Sync Reimbursement By Ref
            .addCase(syncReimbursementByRef.pending, (state) => {
                state.viewDetailsLoading = true;
            })
            .addCase(syncReimbursementByRef.fulfilled, (state) => {
                state.viewDetailsLoading = false;
            })
            .addCase(syncReimbursementByRef.rejected, (state) => {
                state.viewDetailsLoading = false;
            })
            // Download Reimbursement Form
            .addCase(downloadReimbursementForm.pending, (state) => {
                state.viewDetailsLoading = true;
            })
            .addCase(downloadReimbursementForm.fulfilled, (state) => {
                state.viewDetailsLoading = false;
            })
            .addCase(downloadReimbursementForm.rejected, (state, action) => {
                state.viewDetailsLoading = false;
                state.error = action.payload as string;
            })
            // Fetch Associated Reference
            .addCase(fetchReimbursementAssocRef.pending, (state) => {
                state.associatedRefLoading = true;
                state.error = null;
            })
            .addCase(fetchReimbursementAssocRef.fulfilled, (state, action) => {
                state.associatedRefLoading = false;
                state.associatedRefData = action.payload;
            })
            .addCase(fetchReimbursementAssocRef.rejected, (state, action) => {
                state.associatedRefLoading = false;
                state.error = action.payload as string;
            });
    },
});


export const {
    clearError,
    clearCurrentRequest,
    setCurrentRequest,
    setPage,
    setLimit,
    clearRequestTypes,
    clearLTCSchemes,
} = reimbursementSlice.actions;
export default reimbursementSlice.reducer;
